/**
 * @file 左列溢出 tips 列表行组件。
 * 由 `NewRegionSelect` 在 `enableExactOverflowBubble === true` 时渲染一级 region / 页面行。
 */
import React, { useRef, useMemo } from 'react';
import classNames from 'classnames';
import { Checkbox, Icon } from 'tea-component';
import useIsTextOverflow from '@gp/components/EllipsisHover/_hooks/useIsTextOverflow';
import CommonBubble from '../CommonBubble';
import { LOCALE_CN } from './config';
import type { IExactOverflowRegionItemProps } from './type';
import { resolveListBubbleContent } from './util';

/**
 * 左列列表行：仅文本溢出时展示 Bubble tips。
 *
 * DOM 与默认 `CommonBubble` 分支一致，不改变勾选 / 高亮 / 点击行为。
 *
 * @remarks
 * - tips 测量文案为 `rawText (childrenCount)`，与行内展示一致
 * - `listBubbleProps` 来自 `overflowBubbleProps.listRow`，spread 在 `content` 之前由组件补齐默认值
 */
const ExactOverflowRegionItem: React.FC<IExactOverflowRegionItemProps> = ({
  item,
  locale,
  title,
  rawText,
  childrenCount,
  isNotRegionIcon,
  selectedRegion,
  isIndeterminate,
  isChecked,
  leftBubblePlacement,
  leftBubblePlacementOffset,
  listBubbleProps,
  onClickRegionCheckBox,
  onClickRegion,
}) => {
  const hideCount = item?.hideChildrenCount;
  const fullDisplayText = `${rawText} (${childrenCount})`;
  const textRef = useRef<HTMLSpanElement>(null);
  const isOverflow = useIsTextOverflow(textRef, { text: fullDisplayText });
  const bubbleContent = useMemo(
    () => resolveListBubbleContent(
      item,
      locale || LOCALE_CN,
      isOverflow,
      fullDisplayText,
    ),
    [item, locale, isOverflow, fullDisplayText],
  );
  const { value, render, disabled, icon } = item || {};

  return (
    <CommonBubble
      placement={leftBubblePlacement}
      placementOffset={leftBubblePlacementOffset}
      dark
      arrowPointAtCenter
      closeOnScroll
      {...listBubbleProps}
      content={bubbleContent}
    >
      <Checkbox
        key={value}
        className={classNames('dbc-region-select-checkbox', {
          active: value === selectedRegion,
        })}
        onClick={onClickRegionCheckBox}
        display="block"
        disabled={disabled}
        indeterminate={isIndeterminate}
        value={isIndeterminate ? undefined : isChecked}
      >
        {icon && (
          <img
            className={classNames('dbc-region-select-item-icon', {
              'device-icon': isNotRegionIcon,
            })}
            src={icon}
            alt=""
          />
        )}
        <div
          className="dbc-region-select-checkbox-name-wrap"
          onClick={onClickRegion}
        >
          <span ref={textRef} className="dbc-region-select-checkbox-name">
            {render || title}{!hideCount && <>&nbsp;({childrenCount})</>}
          </span>
          <Icon type="arrowright" />
        </div>
      </Checkbox>
    </CommonBubble>
  );
};

export default ExactOverflowRegionItem;
